import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WorkItem from '../components/WorkItem';
import SEO from '../components/SEO';
import { getWorkItems } from '../services/api';
import { ArrowRight } from 'lucide-react';

const Home = () => {
  const [workItems, setWorkItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchWorkItems();
  }, []);

  const fetchWorkItems = async () => {
    try {
      setLoading(true);
      const data = await getWorkItems();
      setWorkItems(data);
    } catch (err) {
      console.error('Error fetching work items:', err);
      setError('Failed to load work items');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f] flex items-center justify-center">
        <div className="text-red-400 text-2xl font-['Poppins'] font-semibold">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f]">
      <SEO
        title="Portfolio - Charles Kilvington | Video Editing & Motion Graphics Work"
        description="Explore Charles Kilvington's portfolio of video editing, motion graphics, and animation projects for clients including Hacker Valley Media, Microsoft, A24, and top music artists."
        keywords="video editing portfolio, motion graphics portfolio, cinematography reel, video editor work samples, animation portfolio"
        ogTitle="Portfolio - Charles Kilvington | Video Editing & Motion Graphics Work"
        ogDescription="Explore video editing, motion graphics, and animation projects for Microsoft, A24, and major music artists."
        ogUrl="https://charleskilvington.com"
        ogImage="https://charleskilvington.com/og-image.jpg"
        twitterTitle="Portfolio - Charles Kilvington"
        twitterDescription="Video editing, motion graphics, and animation work for top-tier clients."
        twitterImage="https://charleskilvington.com/og-image.jpg"
      />
      <Header />
      
      <main className="pt-28 md:pt-36 pb-16 px-8">
        <div className="max-w-[1400px] mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
              className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-purple-600 font-['Poppins'] text-7xl font-black mb-6"
            >
              WORK
            </motion.h2>
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.4, 0.25, 1] }}
              className="h-1 w-32 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto rounded-full"
            />
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
              className="text-gray-400 mt-6 text-lg max-w-2xl mx-auto"
            >
              A collection of creative projects showcasing animation, design, and storytelling
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.25, 0.4, 0.25, 1] }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12"
          >
            {workItems.map((item) => (
              <WorkItem key={item.id} item={item} />
            ))}
          </motion.div>

          {/* Dividing Line */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
            className="flex items-center gap-4 my-20"
          >
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.25, 0.4, 0.25, 1] }}
              className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"
              style={{ transformOrigin: 'left' }}
            />
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
              className="flex items-center gap-3"
            >
              <div className="h-2 w-2 rounded-full bg-purple-500"></div>
              <div className="h-2 w-2 rounded-full bg-pink-500"></div>
              <div className="h-2 w-2 rounded-full bg-purple-500"></div>
            </motion.div>
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.25, 0.4, 0.25, 1] }}
              className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"
              style={{ transformOrigin: 'right' }}
            />
          </motion.div>

          {/* Call to Action to About Page */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-purple-500/10 border border-purple-500/20 px-6 md:px-16 py-12 md:py-16">
              {/* Gradient blur effects */}
              <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl"></div>

              <div className="relative z-10 text-center">
                <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-200 to-pink-200 mb-4 md:mb-6 pb-2">
                  Want to Know More?
                </h2>

                <p className="text-base md:text-xl text-gray-300 mb-8 md:mb-8 max-w-2xl mx-auto">
                  Learn about my journey, expertise, and creative approach.
                </p>

                <button
                  onClick={() => navigate('/about')}
                  className="group inline-flex items-center gap-2 md:gap-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 md:px-10 md:py-5 rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 font-bold text-base md:text-lg shadow-2xl shadow-purple-500/30 hover:scale-105 hover:shadow-purple-500/50 mb-12 md:mb-0"
                >
                  Learn About Me
                  <ArrowRight className="transition-transform duration-300 group-hover:translate-x-2" size={20} />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Home;